import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { conversationService } from '../api/conversationService';
import { 
  Conversation, 
  ConversationCreate, 
  ConversationUpdate,
  ChatMessage,
  ChatMessageCreate,
  MessageSenderRoleEnum 
} from '../types/api';

interface ConversationContextType {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: ChatMessage[];
  hasMore: boolean;
  nextBeforeId?: number;
  isLoading: boolean;
  error: string | null;
  
  // Conversation management
  loadConversations: () => Promise<void>;
  createConversation: (title?: string) => Promise<Conversation | null>;
  selectConversation: (conversationRef: string | number) => Promise<void>;
  updateConversation: (conversationId: number, data: ConversationUpdate) => Promise<void>;
  deleteConversation: (conversationId: number) => Promise<void>;
  clearCurrentConversation: () => void;
  
  // Message management
  loadMessages: (conversationRef: string | number) => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  sendMessage: (content: string, role?: MessageSenderRoleEnum) => Promise<ChatMessage | null>;
  
  // Utility functions
  clearError: () => void;
  searchConversations: (query: string) => Conversation[];
}

const ConversationContext = createContext<ConversationContextType | null>(null);

export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
};

export const ConversationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationState, setCurrentConversationState] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Keep a ref to messages for async flows (e.g., title generation fallback)
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextBeforeId, setNextBeforeId] = useState<number | undefined>(undefined);
  
  /**
   * IMPORTANT: Synchronous conversation reference
   * 
   * React state updates are asynchronous, which causes issues when:
   * 1. createConversation() is called and sets currentConversation state
   * 2. Immediately after, sendMessage() is called but state hasn't updated yet
   * 3. sendMessage() sees currentConversation as null and fails
   * 
   * Solution: Use a ref for immediate synchronous access alongside state for React renders
   * - The ref provides immediate access for operations like sendMessage
   * - The state triggers React re-renders when the conversation changes
   * - Both are always updated together to stay in sync
   */
  const currentConversationRef = React.useRef<Conversation | null>(null);
  
  // Helper to update both ref and state atomically
  // This function should be stable and never change
  const setCurrentConversation = useCallback((conversation: Conversation | null) => {
    console.log('[ConversationContext] setCurrentConversation called', {
      conversationId: conversation?.conversation_id,
      publicId: conversation?.public_id,
      timestamp: Date.now()
    });
    currentConversationRef.current = conversation;
    setCurrentConversationState(conversation);
  }, []);
  
  // Simple in-memory cache for bundles to avoid refetch on re-open
  const bundleCacheRef = React.useRef<Map<number, { 
    conversation: Conversation; 
    messages: ChatMessage[]; 
    hasMore: boolean; 
    nextBeforeId?: number 
  }>>(new Map());
  // Start with loading false - only set true when actually loading
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track which conversations we've attempted title generation for (avoid repeated calls)
  const titleGenAttemptedRef = React.useRef<Set<number>>(new Set());

  const autoTitleConversations = useCallback(async (list: Conversation[]) => {
    // Title generation disabled intentionally
    return;
    try {
      // Select a small number to avoid spamming the backend on first load
      const candidates = list
        .filter((c) => (!c.title || c.title === 'New Chat') && !titleGenAttemptedRef.current.has(c.conversation_id))
        .slice(0, 5);

      if (candidates.length === 0) return;

      await Promise.allSettled(
        candidates.map(async (conv) => {
          titleGenAttemptedRef.current.add(conv.conversation_id);
          try {
            // Add timeout to prevent long waits
            const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
            const titlePromise = conversationService.generateConversationTitle(conv.public_id);
            const updated = await Promise.race([titlePromise, timeoutPromise]);
            
            if (updated) {
              setConversations((prev) => prev.map((c) => (c.conversation_id === updated.conversation_id ? updated : c)));
              if (currentConversationRef.current?.conversation_id === updated.conversation_id) {
                setCurrentConversation(updated);
              }
            } else {
              // Fallback: Generate title from first message client-side
              console.log('API title generation failed or timed out, generating client-side title');
              
              // Try to get first user message for title generation
              const firstUserMessage = messagesRef.current.find(m => m.sender_role === MessageSenderRoleEnum.USER);
              if (firstUserMessage?.message_content) {
                const clientTitle = firstUserMessage.message_content
                  .replace(/[^\w\s]/g, '') // Remove special characters
                  .slice(0, 40) // Take first 40 characters
                  .trim() || 'New Chat';
                
                // Update conversation with client-generated title
                const updatedConv = { ...conv, title: clientTitle };
                setConversations((prev) => prev.map((c) => 
                  c.conversation_id === updatedConv.conversation_id ? updatedConv : c
                ));
                if (currentConversationRef.current?.conversation_id === updatedConv.conversation_id) {
                  setCurrentConversation(updatedConv);
                }
                console.log(`Generated client-side title: "${clientTitle}"`);
              }
            }
          } catch (e) {
            // Non-fatal; skip silently
            console.log('Title generation error:', e);
          }
        })
      );
    } catch {
      // ignore
    }
  }, [setCurrentConversation]); // setCurrentConversation is stable, so this is safe

  // Load all conversations for the current user
  const loadConversations = useCallback(async () => {
    if (!user?.id || !isAuthenticated()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const userConversations = await conversationService.getConversationsByUser(user.id);
      // Optional gating: show only branching-enabled conversations when configured
      const BRANCHING_ONLY = (import.meta as any)?.env?.VITE_BRANCHING_ONLY === 'true';
      const filtered = BRANCHING_ONLY
        ? userConversations.filter((c) => !!(c as any).main_thread_id)
        : userConversations;
      setConversations(filtered.sort((a, b) => 
        new Date(b.last_message_at || b.created_at).getTime() - 
        new Date(a.last_message_at || a.created_at).getTime()
      ));
      // Kick off background title generation for placeholder titles so sidebar reflects updates
      autoTitleConversations(filtered);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, isAuthenticated, autoTitleConversations]); // Include stable autoTitleConversations

  // Create a new conversation
  const createConversation = useCallback(async (title?: string): Promise<Conversation | null> => {
    if (!user?.id || !isAuthenticated()) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      let newConversation = await conversationService.createConversation({
        user_id: user.id,
        title: title || 'New Chat'
      });
      
      // Add to conversations list
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversation(newConversation);
      setMessages([]); // Clear messages for new conversation
      
      return newConversation;
    } catch (err) {
      console.error('Failed to create conversation:', err);
      setError(err.message || 'Failed to create conversation');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, isAuthenticated]);

  // Select and load a conversation
  const selectConversation = useCallback(async (conversationRef: string | number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // If switching to a different conversation, clear current state first
      const previous = currentConversationRef.current;
      const previousRef = previous ? (previous.public_id || String(previous.conversation_id)) : undefined;
      const targetRef = String(conversationRef);
      if (!previousRef || previousRef !== targetRef) {
        setCurrentConversation(null);
        setMessages([]);
        setHasMore(false);
        setNextBeforeId(undefined);
      }

      // Serve from cache if available to avoid loading flicker
      const numericKey = typeof conversationRef === 'number' ? conversationRef : NaN;
      const cached = Number.isFinite(numericKey) ? bundleCacheRef.current.get(numericKey) : undefined;
      if (cached) {
        setCurrentConversation(cached.conversation);
        setMessages(cached.messages);
        setHasMore(cached.hasMore);
        setNextBeforeId(cached.nextBeforeId);
        setIsLoading(false);
        return;
      }

      // Single bundle fetch: conversation + latest messages
      const PAGE_SIZE = 30;
      const bundle = await conversationService.getConversationBundle(conversationRef, PAGE_SIZE);
      setCurrentConversation(bundle.conversation);

      let effectiveMessages = Array.isArray(bundle.messages) ? bundle.messages : [];
      let effectiveHasMore = Boolean(bundle.paging?.hasMore);
      let effectiveNextBeforeId = bundle.paging?.nextCursor?.before_id ? Number(bundle.paging?.nextCursor?.before_id) : undefined;

      // If bundle didn't include messages (or empty due to proxy), try direct messages endpoint as a fallback
      if (!effectiveMessages || effectiveMessages.length === 0) {
        try {
          const fetched = await conversationService.getChatMessages(conversationRef, 0, PAGE_SIZE);
          effectiveMessages = Array.isArray(fetched) ? fetched : [];
          if (effectiveMessages.length > 0) {
            effectiveHasMore = effectiveMessages.length === PAGE_SIZE;
            effectiveNextBeforeId = effectiveHasMore ? Number(effectiveMessages[0]?.message_id) : undefined;
          }
        } catch (fallbackErr) {
          console.warn('Fallback messages fetch failed:', fallbackErr);
        }
      }

      setMessages(effectiveMessages);
      setHasMore(Boolean(effectiveHasMore));
      setNextBeforeId(effectiveNextBeforeId);

      // Populate cache
      if (Number.isFinite(numericKey)) {
        bundleCacheRef.current.set(numericKey, {
          conversation: bundle.conversation,
          messages: effectiveMessages,
          hasMore: Boolean(effectiveHasMore),
          nextBeforeId: effectiveNextBeforeId,
        });
      }
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to select conversation:', err);
      setError(err.message || 'Failed to select conversation');
      setIsLoading(false);
      // Re-throw the error so the caller can handle it
      throw err;
    }
  }, [setCurrentConversation]); // setCurrentConversation is stable

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationRef: string | number) => {
    try {
      // Backwards compatibility: fetch messages if needed (not used during select now)
      const conversationMessages = await conversationService.getChatMessages(conversationRef, 0, 30);
      setMessages(conversationMessages);
      // Do not alter cursor state here
    } catch (err) {
      console.error('Failed to load messages:', err);
      throw err;
    }
  }, []);

  // Load older messages using cursor
  const loadOlderMessages = useCallback(async () => {
    const conversation = currentConversationRef.current;
    if (!conversation || !hasMore || !nextBeforeId) return;
    try {
      const older = await conversationService.getChatMessages(
        conversation.public_id,
        0,
        30,
        nextBeforeId
      );
      if (older.length === 0) {
        setHasMore(false);
        setNextBeforeId(undefined);
        return;
      }
      // Prepend older messages
      setMessages(prev => [...older, ...prev]);
      // Update cache for this conversation
      const cached = bundleCacheRef.current.get(conversation.conversation_id);
      if (cached) {
        bundleCacheRef.current.set(conversation.conversation_id, {
          ...cached,
          messages: [...older, ...cached.messages],
        });
      }
      // Update cursor to the earliest of newly fetched
      const earliest = older[0];
      if (earliest?.message_id) {
        setNextBeforeId(Number(earliest.message_id));
      }
      // If fewer than requested, no more
      if (older.length < 30) {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load older messages:', err);
      setError(err.message || 'Failed to load older messages');
    }
  }, [hasMore, nextBeforeId]);

  // Send a message in the current conversation
  const sendMessage = useCallback(async (content: string, role: MessageSenderRoleEnum = MessageSenderRoleEnum.USER): Promise<ChatMessage | null> => {
    // Use ref for immediate access (fixes async state issue when creating new conversation)
    const conversationToUse = currentConversationRef.current;
    
    if (!conversationToUse) {
      setError('No conversation selected');
      return null;
    }
    
    try {
      const convRef = conversationToUse.public_id;
      if (!convRef) {
        throw new Error('Missing conversation public_id');
      }
      const newMessage = await conversationService.createChatMessage(
        convRef,
        {
          conversation_id: conversationToUse.conversation_id,
          sender_role: role,
          message_content: content
        }
      );
      
      // Add message to local state
      setMessages(prev => [...prev, newMessage]);
      // Update cache with the new message
      if (conversationToUse) {
        const cached = bundleCacheRef.current.get(conversationToUse.conversation_id);
        if (cached) {
          bundleCacheRef.current.set(conversationToUse.conversation_id, {
            ...cached,
            messages: [...cached.messages, newMessage],
          });
        }
      }
      
      // Update conversation's last_message_at locally
      setCurrentConversation(conversationToUse ? {
        ...conversationToUse,
        last_message_at: newMessage.timestamp
      } : null);
      
      // Update in conversations list
      setConversations(prev => prev.map(conv => 
        conv.conversation_id === conversationToUse.conversation_id
          ? { ...conv, last_message_at: newMessage.timestamp }
          : conv
      ));
      
      // Title generation disabled: compute client-side placeholder if missing
      const isPlaceholder = !conversationToUse.title || conversationToUse.title === 'New Chat';
      if (role === MessageSenderRoleEnum.USER && isPlaceholder) {
        const clientTitle = content
          .replace(/[^\w\s]/g, '')
          .slice(0, 40)
          .trim() || 'New Chat';
        const updatedConv = { ...conversationToUse, title: clientTitle };
        setConversations(prev => prev.map(conv => 
          conv.conversation_id === updatedConv.conversation_id ? updatedConv : conv
        ));
        if (currentConversationRef.current?.conversation_id === updatedConv.conversation_id) {
          setCurrentConversation(updatedConv);
        }
      }

      return newMessage;
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.message || 'Failed to send message');
      return null;
    }
  }, []); // Remove setCurrentConversation dependency to prevent re-creation

  // Update conversation (e.g., rename)
  const updateConversation = useCallback(async (conversationId: number, data: ConversationUpdate) => {
    try {
      const ref = conversations.find(c => c.conversation_id === conversationId)?.public_id || conversationId;
      const updated = await conversationService.updateConversation(ref, data);
      
      // Update in local state
      setConversations(prev => prev.map(conv => 
        conv.conversation_id === conversationId ? updated : conv
      ));
      
      if (currentConversationRef.current?.conversation_id === conversationId) {
        setCurrentConversation(updated);
      }
    } catch (err) {
      console.error('Failed to update conversation:', err);
      setError(err.message || 'Failed to update conversation');
    }
  }, [conversations]); // Add conversations dependency, remove setCurrentConversation

  // Delete a conversation
  const deleteConversation = useCallback(async (conversationId: number) => {
    try {
      const ref = conversations.find(c => c.conversation_id === conversationId)?.public_id || conversationId;
      await conversationService.deleteConversation(ref);
      
      // Remove from local state
      setConversations(prev => prev.filter(conv => conv.conversation_id !== conversationId));
      
      // Clear current if it was deleted
      if (currentConversationRef.current?.conversation_id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      setError(err.message || 'Failed to delete conversation');
    }
  }, [conversations]); // Add conversations dependency, remove setCurrentConversation

  // Clear current conversation (for new chat)
  const clearCurrentConversation = useCallback(() => {
    setCurrentConversation(null);
    setMessages([]);
    setHasMore(false);
    setNextBeforeId(undefined);
  }, [setCurrentConversation]); // setCurrentConversation is stable

  // Search conversations
  const searchConversations = useCallback((query: string): Conversation[] => {
    const lowerQuery = query.toLowerCase();
    return conversations.filter(conv => 
      conv.title?.toLowerCase().includes(lowerQuery) || false
    );
  }, [conversations]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Set loading to false on first mount if no user
  useEffect(() => {
    if (!user?.id && isLoading) {
      setIsLoading(false);
    }
  }, [user?.id, isLoading]); // Add isLoading dependency
  
  // Load conversations when user logs in
  // Store functions in refs to avoid dependency issues
  const loadConversationsRef = useRef(loadConversations);
  const isAuthenticatedRef = useRef(isAuthenticated);
  
  useEffect(() => {
    loadConversationsRef.current = loadConversations;
    isAuthenticatedRef.current = isAuthenticated;
  });
  
  useEffect(() => {
    console.log('[ConversationContext] User auth effect running', {
      userId: user?.id,
      isAuth: isAuthenticatedRef.current(),
      timestamp: Date.now()
    });
    
    if (user?.id && isAuthenticatedRef.current()) {
      loadConversationsRef.current();
    } else if (!user?.id) {
      // Clear data when user logs out
      console.log('[ConversationContext] Clearing data - no user');
      setConversations([]);
      setCurrentConversation(null);
      setMessages([]);
    }
  }, [user?.id, setCurrentConversation]); // Add setCurrentConversation which is stable

  const value: ConversationContextType = useMemo(() => ({
    conversations,
    currentConversation: currentConversationState,  // Expose state for React components
    messages,
    hasMore,
    nextBeforeId,
    isLoading,
    error,
    loadConversations,
    createConversation,
    selectConversation,
    updateConversation,
    deleteConversation,
    clearCurrentConversation,
    loadMessages,
    loadOlderMessages,
    sendMessage,
    clearError,
    searchConversations
  }), [
    // State values that need to trigger re-renders
    conversations,
    currentConversationState,
    messages,
    hasMore,
    nextBeforeId,
    isLoading,
    error,
    // Stable callbacks - these should never change
    loadConversations,
    createConversation,
    selectConversation,
    updateConversation,
    deleteConversation,
    clearCurrentConversation,
    loadMessages,
    loadOlderMessages,
    sendMessage,
    clearError,
    searchConversations
  ]);

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};

export default ConversationContext;
